// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';

const CURRENCIES = [
  { code: 'USD', symbol: '$', price: '20' },
  { code: 'EUR', symbol: '€', price: '20' },
  { code: 'GBP', symbol: '£', price: '15' },
  { code: 'CAD', symbol: '$', price: '25' },
];

export default function Pricing({ isFr, setView }) {
  const [currency, setCurrency] = useState('USD');
  const activeIndex = CURRENCIES.findIndex((c) => c.code === currency);
  const active = CURRENCIES[activeIndex];

  const features = isFr
    ? [
        'Calcul automatique des recettes',
        'Traçabilité complète',
        'Boutique en ligne incluse',
        'Commandes illimitées',
        'Aucune commission',
        'Plusieurs points de vente',
      ]
    : [
        'Automatic recipe scaling',
        'Complete traceability',
        'Online shop included',
        'Unlimited orders',
        'No commissions',
        'Multi-location support',
      ];

  return (
    <section id="pricing" className="dbk-section dbk-section--alt">
      <div className="dbk-wrap">
        <div className="dbk-head">
          <h2 className="dbk-h2">{isFr ? 'Tarif simple et transparent' : 'Simple, transparent pricing'}</h2>
          <p className="dbk-lede">
            {isFr ? 'Un seul prix, toutes les fonctionnalités incluses.' : 'One price, every feature included.'}
          </p>
        </div>

        <div className="dbk-price">
          <div className="dbk-price__card">
            <div
              className="dbk-seg"
              role="group"
              aria-label={isFr ? 'Devise' : 'Currency'}
              style={{ '--seg-i': activeIndex }}
            >
              <span className="dbk-seg__thumb" aria-hidden="true" />
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  aria-pressed={currency === c.code}
                  onClick={() => setCurrency(c.code)}
                >
                  {c.code}
                </button>
              ))}
            </div>

            <div className="dbk-price__amount">
              <span className="dbk-price__num">{active.symbol}{active.price}</span>
              <span className="dbk-price__per">{isFr ? '/ mois' : '/ month'}</span>
            </div>
            <p className="dbk-price__trial">
              {isFr ? '30 jours d’essai gratuit · Sans carte bancaire' : '30-day free trial · No credit card required'}
            </p>

            <button
              type="button"
              className="dbk-btn dbk-btn--primary dbk-btn--block"
              onClick={() => setView('signup-baker')}
            >
              {isFr ? 'Commencer l’essai de 30 jours' : 'Start your 30-day trial'}
              <ArrowRight size={18} strokeWidth={2} />
            </button>

            <ul className="dbk-price__list">
              {features.map((f) => (
                <li key={f}><Check size={16} strokeWidth={2.25} />{f}</li>
              ))}
            </ul>

            <div className="dbk-price__fine">
              <p className="dbk-fine">
                {isFr ? 'Annulez à tout moment. Pas de contrat, pas de frais d’annulation.' : 'Cancel anytime. No contract, no cancellation fees.'}
              </p>
              <p className="dbk-fine">
                {isFr
                  ? 'Avec le paiement Stripe, les frais Stripe habituels s’appliquent selon votre pays.'
                  : 'If you use Stripe payment, standard Stripe fees apply depending on your country.'}
              </p>
            </div>
          </div>

          <aside className="dbk-price__aside">
            <p>
              {isFr
                ? 'Toutes les fonctionnalités sont dans le forfait. Pas d’options ni de paliers à débloquer plus tard.'
                : 'Every feature is in the plan. No add-ons, no tiers to unlock later.'}
            </p>
            <p className="dbk-fine">
              {isFr
                ? 'Données chiffrées sur Google Cloud · Enregistrements de sécurité alimentaire pour la France, le Canada, les États-Unis et le Royaume-Uni.'
                : 'Encrypted data on Google Cloud · Food-safety records for France, Canada, the USA and the UK.'}
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}
